import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import OnboardingLayout from "../../../components/layout/OnboardingLayout";
import Button from "../../../components/ui/Button";
import FileUpload from "../../../components/ui/FileUpload";
import { getRequiredDocuments, uploadKycFile } from "../../../services/api";

const DEFAULT_STEPS = ["Business Details", "Verification", "Done"];

// Human-readable labels for docType codes returned by the API
const DOC_LABELS = {
  NATIONAL_ID_CARD: "National ID Card",
  PASSPORT: "Passport",
  DRIVERS_LICENSE: "Driver's License",
  UTILITY_BILL: "Utility Bill",
  BANK_STATEMENT: "Bank Statement",
  BUSINESS_REGISTRATION: "Business Registration Certificate",
  CERTIFICATE_OF_INCORPORATION: "Certificate of Incorporation",
  DIRECTOR_ID: "Director ID",
};

const docLabel = (type) => DOC_LABELS[type] || type.replace(/_/g, " ");

// Normalise the API response into a plain array of docType strings.
// Handles both: ["PASSPORT", ...] and [{ docType: "PASSPORT", ... }, ...]
const normaliseDocList = (raw) => {
  if (!Array.isArray(raw)) return [];
  return raw.map((item) =>
    typeof item === "string" ? item : item.docType || item.type || ""
  ).filter(Boolean);
};

// Common OFFRAMP destination countries (ISO 3166-1 alpha-2)
const OFFRAMP_COUNTRIES = [
  { code: "AE", label: "UAE" },
  { code: "NG", label: "Nigeria" },
  { code: "KE", label: "Kenya" },
  { code: "GH", label: "Ghana" },
  { code: "ZA", label: "South Africa" },
  { code: "EG", label: "Egypt" },
  { code: "IN", label: "India" },
  { code: "GB", label: "United Kingdom" },
  { code: "US", label: "United States" },
];

/**
 * KYB onboarding form.
 *
 * Flow:
 *  1. Receiver selects the OFFRAMP destination country.
 *  2. GET /api/v2/client/kyc/required-documents → list of required docType strings.
 *  3. User uploads each required document.
 *  4. POST /api/v2/client/kyc (multipart) for each document → upload + submit in one call.
 *  5. Navigate to /onboarding/kyb/pending.
 */
const KYBForm = () => {
  const navigate = useNavigate();
  const userId = useSelector((s) => s.auth.userId);
  const role = useSelector((s) => s.auth.role);
  const userCountry = useSelector((s) => s.auth.country);

  const transactionType = role === "receiver" ? "OFFRAMP" : "ONRAMP";
  const isReceiver = role === "receiver";

  // ── Country selection (receiver only) ────────────────────────────────────
  // Senders default to "NG" for ONRAMP; receivers must explicitly pick.
  const [country, setCountry] = useState(isReceiver ? (userCountry || "") : (userCountry || "NG"));
  const [countryError, setCountryError] = useState("");

  // ── Required documents ────────────────────────────────────────────────────
  const [requiredDocs, setRequiredDocs] = useState([]);
  const [schemaLoading, setSchemaLoading] = useState(!isReceiver); // auto-load for senders
  const [schemaLoaded, setSchemaLoaded] = useState(false);
  const [schemaError, setSchemaError] = useState(false);

  // ── File state ────────────────────────────────────────────────────────────
  const [files, setFiles] = useState({});
  const [fileErrors, setFileErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState("");

  // Auto-load for senders (country already set to userCountry or "NG")
  useEffect(() => {
    if (!isReceiver) {
      fetchDocs(userCountry || "NG");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchDocs = async (countryCode) => {
    setSchemaLoading(true);
    setSchemaError(false);
    setSchemaLoaded(false);
    try {
      const res = await getRequiredDocuments({ transactionType, country: countryCode });
      const docs = normaliseDocList(res?.data);
      if (docs.length === 0) {
        setSchemaError(true);
      } else {
        setRequiredDocs(docs);
        setSchemaLoaded(true);
      }
    } catch {
      setSchemaError(true);
    } finally {
      setSchemaLoading(false);
    }
  };

  // Receiver: validate country then fetch docs
  const handleLoadDocs = () => {
    const trimmed = country.trim().toUpperCase();
    if (!trimmed || trimmed.length !== 2) {
      setCountryError("Enter a valid 2-letter ISO country code (e.g. AE, NG).");
      return;
    }
    setCountryError("");
    setCountry(trimmed);
    fetchDocs(trimmed);
  };

  const handleFileChange = (docType, file) => {
    setFiles((prev) => ({ ...prev, [docType]: file }));
    setFileErrors((prev) => ({ ...prev, [docType]: "" }));
  };

  const validate = () => {
    const errs = {};
    requiredDocs.forEach((docType) => {
      if (!files[docType]) errs[docType] = `${docLabel(docType)} is required.`;
    });
    setFileErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    setSubmitError("");

    try {
      // POST /api/v2/client/kyc — upload is the submission, no separate step
      for (const docType of requiredDocs) {
        const file = files[docType];
        if (!(file instanceof File)) continue;
        await uploadKycFile({
          file,
          docType,
          userId,
          transactionType,
          side: "front",
        });
      }
      navigate("/onboarding/kyb/pending");
    } catch (err) {
      setSubmitError(
        err?.response?.data?.message || "Submission failed. Please try again."
      );
    } finally {
      setSubmitting(false);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────
  return (
    <OnboardingLayout steps={DEFAULT_STEPS} currentStep={0}>
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm p-6">
        <h1 className="text-xl font-bold text-gray-900 mb-1">Business Verification</h1>
        <p className="text-sm text-gray-500 mb-6">
          Upload the required documents for compliance verification.
        </p>

        {/* ── Country selector (receiver only) ─────────────────────── */}
        {isReceiver && (
          <div className="mb-6 pb-6 border-b border-gray-100">
            <label className="block text-sm font-medium text-gray-700 mb-1.5">
              OFFRAMP Destination Country
              <span className="text-red-500 ml-0.5">*</span>
            </label>
            <p className="text-xs text-gray-400 mb-3">
              Select the country where you want to receive funds. Document requirements vary by country.
            </p>

            {/* Quick-select common countries */}
            <div className="flex flex-wrap gap-2 mb-3">
              {OFFRAMP_COUNTRIES.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    setCountry(c.code);
                    setCountryError("");
                  }}
                  className={`px-3 py-1.5 text-xs rounded-lg border transition-colors ${
                    country === c.code
                      ? "bg-brand text-white border-brand"
                      : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                  }`}
                >
                  {c.label} ({c.code})
                </button>
              ))}
            </div>

            <div className="flex gap-2 items-start">
              <div className="flex-1">
                <input
                  type="text"
                  value={country}
                  onChange={(e) => {
                    setCountry(e.target.value.toUpperCase());
                    setCountryError("");
                  }}
                  placeholder="or type ISO code e.g. AE"
                  maxLength={2}
                  className={`w-full border rounded-xl px-4 py-2.5 text-sm text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-brand/30 focus:border-brand uppercase ${
                    countryError ? "border-red-400" : "border-gray-200"
                  }`}
                />
                {countryError && (
                  <p className="mt-1 text-xs text-red-500">{countryError}</p>
                )}
              </div>
              <button
                type="button"
                onClick={handleLoadDocs}
                disabled={schemaLoading}
                className="px-4 py-2.5 text-sm font-medium bg-brand text-white rounded-xl hover:bg-brandHover transition-colors disabled:opacity-60 whitespace-nowrap"
              >
                {schemaLoading ? "Loading…" : "Load Requirements"}
              </button>
            </div>
          </div>
        )}

        {/* ── Schema loading spinner ────────────────────────────────── */}
        {schemaLoading && (
          <div className="space-y-5">
            {[1, 2].map((i) => (
              <div key={i} className="animate-pulse">
                <div className="h-4 bg-gray-100 rounded w-1/3 mb-2" />
                <div className="h-24 bg-gray-100 rounded-xl" />
              </div>
            ))}
          </div>
        )}

        {/* ── Schema error ──────────────────────────────────────────── */}
        {!schemaLoading && schemaError && (
          <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
            <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
              <svg className="w-5 h-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <p className="text-sm font-semibold text-gray-800 mb-1">
              Could not load document requirements
            </p>
            <p className="text-xs text-gray-500 mb-4">
              Please check the country code and try again, or contact support.
            </p>
            <button
              type="button"
              onClick={() => fetchDocs(country)}
              className="text-sm text-brand font-medium hover:underline"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Document upload form ──────────────────────────────────── */}
        {!schemaLoading && schemaLoaded && requiredDocs.length > 0 && (
          <>
            {/* Confirmation of selected country */}
            {isReceiver && (
              <div className="flex items-center gap-2 mb-5 px-3 py-2 bg-blue-50 border border-blue-100 rounded-lg">
                <svg className="w-4 h-4 text-brand flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-brand">
                  Showing requirements for <strong>{country}</strong> ({transactionType})
                </p>
                <button
                  type="button"
                  onClick={() => { setSchemaLoaded(false); setFiles({}); setFileErrors({}); }}
                  className="ml-auto text-xs text-brand underline"
                >
                  Change
                </button>
              </div>
            )}

            {submitError && (
              <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 rounded-lg px-4 py-3 mb-5 text-sm">
                <svg className="w-4 h-4 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                {submitError}
              </div>
            )}

            <form onSubmit={handleSubmit} noValidate>
              <div className="space-y-5">
                {requiredDocs.map((docType) => (
                  <FileUpload
                    key={docType}
                    label={docLabel(docType)}
                    name={docType}
                    accept={["application/pdf", "image/png", "image/jpeg", "image/jpg"]}
                    maxSizeMB={10}
                    hint="PDF, PNG or JPG · Max 10 MB"
                    error={fileErrors[docType]}
                    onChange={(file) => handleFileChange(docType, file)}
                    required
                  />
                ))}
              </div>

              <div className="mt-8">
                <Button type="submit" loading={submitting}>
                  Submit Documents
                </Button>
              </div>
            </form>
          </>
        )}
      </div>
    </OnboardingLayout>
  );
};

export default KYBForm;
